import React, { useState } from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAudioPlayerContext } from '@/context/AudioPlayerContext';
import { AudioItem } from '@/components/ui/audio-player';
import { audioService } from '@/services/AudioService';

interface AudioReadingButtonProps {
    audios?: AudioItem[];
    variant?: 'contained' | 'outlined';
    sx?: any;
    className?: string;
    readingId?: string;
}

const AudioReadingButton: React.FC<AudioReadingButtonProps> = ({
    audios,
    variant = 'contained',
    sx,
    className,
    readingId
}) => {
    const { t } = useTranslation();
    const { setAudios, showPlayer } = useAudioPlayerContext();
    const [isLoading, setIsLoading] = useState(false);

    const startAudioReading = async () => {
        if (isLoading) return;

        // If audios are provided, use them directly
        if (audios) {
            setAudios(audios);
            showPlayer();
            return;
        }

        // If no readingId is provided, use fallback audios
        if (!readingId) {
            const fallbackAudios: AudioItem[] = [
                {
                    section: "Introduction",
                    url: "https://www.soundjay.com/misc/sounds/bell-ringing-05.wav"
                },
                {
                    section: "Card 1 - Past",
                    url: "https://www.soundjay.com/footsteps/sounds/footsteps-1.mp3"
                },
                {
                    section: "Card 2 - Present",
                    url: "https://www.soundjay.com/misc/sounds/bell-ringing-04.wav"
                },
                {
                    section: "Card 3 - Future",
                    url: "https://www.soundjay.com/human/sounds/man-laughing-01.mp3"
                },
                {
                    section: "Conclusion",
                    url: "https://www.soundjay.com/nature/sounds/stream-3.mp3"
                }
            ];
            setAudios(fallbackAudios);
            showPlayer();
            return;
        }

        setIsLoading(true);

        try {
            // First try to get existing audio
            const existingAudio = await audioService.getReadingAudio(readingId);
            console.log('Existing audio response:', existingAudio);

            if (existingAudio.success && existingAudio.audioUrls && existingAudio.audioUrls.length > 0) {
                // Use existing audio
                const audioItems: AudioItem[] = existingAudio.audioUrls.map((url: string, index: number) => {
                    const fullUrl = `${import.meta.env.VITE_API_URL}${url}`;
                    console.log(`Audio URL ${index + 1}:`, fullUrl);
                    return {
                        section: `Reading Part ${index + 1}`,
                        url: fullUrl
                    };
                });
                console.log('Final audio items:', audioItems);
                setAudios(audioItems);
                showPlayer();
            } else {
                console.log('No existing audio found, generating new audio...');
                // Generate new audio
                const result = await audioService.generateReadingAudio(readingId);
                console.log('Audio generation result:', result);

                if (result.success && result.audioUrls && result.audioUrls.length > 0) {
                    const audioItems: AudioItem[] = result.audioUrls.map((url: string, index: number) => {
                        const fullUrl = `${import.meta.env.VITE_API_URL}${url}`;
                        console.log(`Generated audio URL ${index + 1}:`, fullUrl);
                        return {
                            section: `Reading Part ${index + 1}`,
                            url: fullUrl
                        };
                    });
                    console.log('Final generated audio items:', audioItems);
                    setAudios(audioItems);
                    showPlayer();
                } else {
                    console.error('Failed to generate audio:', result.error);
                    // You might want to show an error message to the user here
                }
            }
        } catch (error) {
            console.error('Error in startAudioReading:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsLoading(false);
        }
    };

    const defaultSx = variant === 'contained' ? {
        position: 'fixed',
        bottom: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 48px)',
        maxWidth: '600px',
        py: 1.5,
        fontSize: '1.1rem',
        fontFamily: '"Playfair Display", serif',
        background: 'linear-gradient(135deg, #2B2E4A 0%, #3D4066 100%)',
        '&:hover': {
            background: 'linear-gradient(135deg, #3D4066 0%, #2B2E4A 100%)',
        }
    } : {
        borderColor: '#2B2E4A',
        color: '#2B2E4A',
        '&:hover': {
            borderColor: '#3D4066',
            backgroundColor: 'rgba(43, 46, 74, 0.08)',
        }
    };

    return (
        <MuiButton
            variant={variant}
            onClick={startAudioReading}
            disabled={isLoading}
            sx={{ ...defaultSx, ...sx }}
            className={className}
        >
            {isLoading ? (
                <>
                    <CircularProgress
                        size={20}
                        sx={{
                            color: variant === 'contained' ? 'white' : '#2B2E4A',
                            mr: 1
                        }}
                    />
                    {t('readingPage.loadingReading')}
                </>
            ) : (
                t('readingPage.startAudioReading')
            )}
        </MuiButton>
    );
};

export default AudioReadingButton; 